import React from 'react';
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import type { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ViewMode } from '../hooks/usePersistentViewMode';

interface ViewModeToggleProps
  extends Omit<ToggleButtonGroupProps, 'exclusive' | 'value' | 'onChange'> {
  value: ViewMode;
  onChange: (value: ViewMode) => void;
  ariaLabel?: string;
  /** True when this surface overrides the account default view. */
  isOverride?: boolean;
  /** Supplied together with isOverride to offer a "use default" reset. */
  onUseDefault?: () => void;
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  value,
  onChange,
  ariaLabel = 'view mode',
  size = 'small',
  isOverride,
  onUseDefault,
  ...toggleGroupProps
}) => {
  const group = (
    <ToggleButtonGroup
      {...toggleGroupProps}
      exclusive
      size={size}
      value={value}
      onChange={(_, nextValue: ViewMode | null) => {
        if (nextValue) onChange(nextValue);
      }}
      aria-label={ariaLabel}
    >
      <Tooltip title="Grid view">
        <ToggleButton value="grid" aria-label="Grid view">
          <ViewModuleIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="List view">
        <ToggleButton value="list" aria-label="List view">
          <ViewListIcon fontSize="small" />
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );

  // Only offered once this surface actually diverges from the account default.
  if (!isOverride || !onUseDefault) {
    return group;
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {group}
      <Tooltip title="Use default view">
        <IconButton size={size} aria-label="Use default view" onClick={onUseDefault}>
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ViewModeToggle;
